export type Success = () => Success;
export type IError = (error: Error) => IError;
export type PearsonToken = () => Promise<string>;
export type AuthToken = (callback: (token: string) => void) => void;
export enum RendererTypes {
  WEB = "WEB",
  MOBILE = "MOBILE",
}

export enum ENVIRONMENTS {
  DEV = "dev",
  QA = "qaint",
  STG = "stg",
  PPE = "ppe",
  PROD = "prod",
}

export interface Ancestry {
  elementId: string;
  elementType: string;
}
// TODO: Need to remove the screenId from the subTarget once the full web toc map is implemented BRNT-29613
export interface Route {
  id: string;
  type?: string;
  cohortId?: string;
  deploymentId?: string;
  ancestry?: Ancestry[];
  subTarget?: { ref: string; elementRef: string; screenId?: string };
}
export interface Media {
  mediaType: "Image" | "Video" | "Audio";
  metadata: Image | Video | Audio;
}

export interface ScrollData {
  activeScreenId: string;
  dataId: string;
  scrollDirection: "up" | "down";
}

export interface PageData {
  pageNumber: string;
}

export interface Image {
  src: string;
  title?: string;
  caption?: string;
  alt?: string;
}

export interface Video {
  id: string;
  type: "Video";
  description: string;
  title: string;
  anonymous: boolean;
  embeddedMode: boolean;
  alt: string;
  src: string;
  duration: number;
  thumbnail: {
    type: "Image";
    src: string;
    height: number;
    width: number;
  };
  transcript: [
    {
      lang: string;
      src: string;
      title: string;
    },
  ];
}

export interface Audio {
  action: string;
  title: string;
  url: string;
  locale: string;
  duration: number;
  transcript: string;
  type: string;
}

export interface Link {
  type: "ExternalLink";
  href: string;
}

export interface MousePosition {
  positionX: number;
  positionY: number;
}

export interface SelectionRect {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface IAnnotation {
  elementId?: string; // ID of the element that the text is selected
  screenId?: string; // screen Id
  selectedText?: string;
  bookmark: {
    start: number;
    end: number;
    backward: boolean;
    commonAncestorCssSelector: string;
  };
  color?: string;
  annotationId?: string;
  annotationType?: string;
  noteText?: string;
  mousePosition?: MousePosition;
  selectionRect?: SelectionRect;
  isOverlappedSelection?: boolean;
  scrollIntoView?: boolean;
  highlight?: AnnotationMathmlPayload;
  dataElementId: string; //  ID of the closest element with the data-id attribute that the text is selected
}

export interface IAnnotationPopoverData {
  annotationType: "Note" | "Flashcard";
  mousePosition: MousePosition;
  annotations: IAnnotation[];
}

export type ViewerConfig = {
  domElement: HTMLElement;
  cohortId: string;
  deploymentId: string;
  pearsonUid: string;
  getAuthToken: AuthToken;
  isInstructor?: boolean;
  packagePath?: string;
  renderer?: RendererTypes;
  env?: ENVIRONMENTS;
  decrypt?: (screenId: string, filesList: string[], onComplete: () => void) => void;
  isOnline?: () => boolean;
  events?: ViewerEvents;
};

export interface ViewerEvents {
  onReady?: () => void;
  onRouteChange?: (data: Route) => void;
  onRouteComplete?: (data: Route) => void;
  onLinkClick?: (data: Link) => void;
  onAnnotationRequest?: (data: IAnnotation) => void;
  onAnnotationIconClick?: (data: IAnnotationPopoverData) => void;
  onMediaClick?: (data: Media) => void;
  onMediaPlay?: (data: Media) => void;
  onPageScroll?: (data: ScrollData) => void;
  onPageChange?: (data: PageData) => void;
  onPageClick?: () => void;
}

export interface BronteViewer {
  viewerConfig: ViewerConfig;
  services: {
    coursewareStructureService: any;
    cdpServices: any;
  };
}

export type Theme = {
  theme: string;
  cssText: string;
};

export interface EventMetaData {
  coursewareId: string;
  coursewareType: string;
}

interface AnnotationMathmlPayload {
  hasMathml: boolean;
  content: Array<MathMlPayloadContent>;
}

interface MathMlPayloadContent {
  type: string;
  content: {
    schema?: string;
    text: string;
    fallbackImage?: {
      id: string;
      type: string;
      alt: string;
      src: string;
      height: number;
      width: number;
    };
  };
}

export enum OneTrustCookieKeys {
  C0001 = "C0001",
  C0002 = "C0002",
  C0003 = "C0003",
  C0004 = "C0004",
}
export type OneTrustCookieCategories = {
  [key in OneTrustCookieKeys]: string;
};

export type CookieSpecifications = {
  [key: string]: boolean;
};

export type PiSessionData = {
  piAuthExpiryTime: string;
  piAuthSession: string;
  piDb: string;
  piSessionId: string;
  piDeviceId: string;
  piHomeCountryCode: string;
  piLang: string;
};
